import { useSyncExternalStore } from 'react';
import { useRecoilValue } from 'recoil';
import { RoiStatistics } from '~/engine/util/algorithms/sdf/ContourStatistics';
import { useDomainStore } from '~/hooks/domain/useDomainStore';
import { environmentState } from '~/state/atoms/engineAtoms';

/**
 * Reactively reads the SDF-derived shape statistics (surface area, sphericity,
 * center of mass, bounding box) for an ROI.
 *
 * Subscribes to the domain store — the same seam {@link useRoiDetailView} uses
 * for volume — so the panel refreshes whenever a contour edit bumps the store
 * version (a brush stroke commits via the contour service → `markChanged`).
 * The engine self-invalidates the stats cache on SDF data-version, so the
 * re-read returns fresh values; an unchanged ROI returns the same cached
 * object, so unrelated domain changes don't churn the panel.
 *
 * Returns undefined for point ROIs, empty ROIs, or before the environment is
 * ready.
 */
export function useRoiStatistics(roiNumber: number): RoiStatistics | undefined {
    const env = useRecoilValue(environmentState);
    const store = useDomainStore();
    return useSyncExternalStore(store.subscribe, () =>
        env?.getRoiStatistics(roiNumber)
    );
}
